import type { RouteLocationNormalized, RouteRecordName } from 'vue-router'
import { useAppConfiguration } from '~/stores/appConfiguration'
import { useWorkspace } from '~/stores/workspace'

const getSignInRedirectPath = (
  to: RouteLocationNormalized,
  windowLocation: Location,
) => {
  // dropping down to use location to redirect. navigateTo stalls when used client side
  if (to.name === 'index') {
    windowLocation.replace('/auth/login')
    return
  }

  const fromUrl = new URL(windowLocation.href)
  fromUrl.pathname = '/auth/login'

  const allowedReturnPageNames: RouteRecordName[] = [
    'changelog',
    'claims',
    'claims-id',
    'claims-id-insightId-influencing-claims',
    'dashboard',
    'exclusions',
    'exclusions-id',
    'policies',
    'policies-id',
    'policies-id-providers-impacted',
    'account-api-clients',
  ]

  if (to.name && allowedReturnPageNames.includes(to.name)) {
    const query = new URLSearchParams(to.query as Record<string, string>)
    const path = query.size ? `${to.path}?${query.toString()}` : to.path
    fromUrl.search = new URLSearchParams({
      from: encodeURIComponent(path),
    }).toString()
  }

  windowLocation.replace(fromUrl)
  return
}

const hasConsoleViewPermission = (
  userPermissions: string[] = [],
  workspaceId?: string,
): boolean => {
  return workspaceId
    ? userPermissions.some(
        (p) =>
          p === `workspace:${workspaceId}#read:connector-config` ||
          p === `workspace:${workspaceId}#read:system`,
      )
    : userPermissions.some(
        (p) =>
          p.endsWith('#read:connector-config') || p.endsWith('#read:system'),
      )
}

export default defineNuxtRouteMiddleware(async (to) => {
  const { $auth0, $auth0Ready, $pinia } = useNuxtApp()
  const currentUser = useCurrentUser()
  const workspace = useWorkspace($pinia)

  if (to.name === 'welcome') {
    currentUser.value = await workspace.getUser()
    return
  }

  if (to.meta.public) {
    return
  }

  const tenantConfig = useAppConfig().tenant

  if (import.meta.client) {
    const { isAuthenticated } = $auth0()

    await $auth0Ready()

    if (!isAuthenticated.value) {
      return navigateTo('/auth/login')
    }

    try {
      const connectorId = to.query.lob as string

      currentUser.value = await workspace.getUser(connectorId)

      const appConfig = useAppConfiguration()
      const appConfigUnleashed = useAppConfigurationUnleashed()

      await appConfig.getAll() //await is necessary, do not remove
      await appConfigUnleashed.getAll() //TODO: this initializes the stores, might be better to poll.

      if (
        appConfig.maintenance &&
        to.name !== 'welcome' &&
        !workspace.isRialticUser
      ) {
        return navigateTo('/maintenance')
      }

      const canAccessConsole = hasConsoleViewPermission(
        currentUser.value?.permissions,
        tenantConfig.singleTenantWorkspaceId,
      )

      if (!canAccessConsole) {
        return navigateTo('/welcome')
      }
      if (!currentUser.value?.connectors.length) {
        if (/^account/.test(to.name as string)) {
          return
        }
        return navigateTo('/account')
      }
    } catch (_error) {
      return getSignInRedirectPath(to, location)
    }
  }
})
